import React, { Suspense } from "react";
import { useField } from "formik";
import { Box, Skeleton } from "@mui/material";

// WHEELHOUSE #6 — build-time-gated code-split of the BUILDER TinyMCE editor
// (WL-069 / PERF-F1). TinyMCE (~8 MB on disk) is statically imported by
// ../RichTextEditor, so — together with the read-only viewer editor — it lands
// in the eager bundle. This is the SECOND of TinyMCE's two static importers;
// splitting BOTH is required to move TinyMCE out of the main chunk.
//
// Gate: REACT_APP_LAZY_RICH_TEXT (defaults OFF -> byte-parity static require).
// The specific key is made a DefinePlugin constant in craco.config.js so webpack
// folds the condition and prunes the dead branch's dependency. ON ->
// React.lazy(() => import()) so TinyMCE splits into an on-demand chunk fetched
// only when a text block is actually edited; a height-reserved <Skeleton> covers
// the async gap (CLS-safe, prefers-reduced-motion aware).
let RichTextEditor;
if (process.env.REACT_APP_LAZY_RICH_TEXT === "true") {
  const LazyEditor = React.lazy(() => import("../RichTextEditor"));
  RichTextEditor = function RichTextEditorLazy(props) {
    return (
      <Suspense
        fallback={
          <Box
            role="status"
            aria-busy="true"
            aria-label="Loading editor"
            sx={{
              width: "100%",
              minHeight: 220,
              p: 2,
              "@media (prefers-reduced-motion: reduce)": {
                "& .MuiSkeleton-root": { animation: "none" },
              },
            }}
          >
            <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 1, borderRadius: 1 }} />
            {[96, 88, 92, 70].map((w, i) => (
              <Skeleton key={i} variant="text" animation="wave" width={`${w}%`} sx={{ fontSize: "1.1rem" }} />
            ))}
          </Box>
        }
      >
        <LazyEditor {...props} />
      </Suspense>
    );
  };
} else {
  // eslint-disable-next-line global-require
  RichTextEditor = require("../RichTextEditor").default;
}

const FormikRichTextEditor = ({
  name,
  id,
  onChange,
  onBlur,
  disabled,
  handleEditorSetup,
  handleSetContent,
  editorRef,
}) => {
  const [field, meta, helpers] = useField(name);
  const { value } = field;
  const { touched, error } = meta;
  const { setValue, setTouched } = helpers;

  const handleChange = (text) => {
    setValue(text);
    if (onChange) {
      onChange(name, text);
    }
  };

  const handleBlur = () => {
    setTouched(true);
    if (onBlur) onBlur(name, value);
  };

  return (
    <RichTextEditor
      name={name}
      id={id}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={touched ? error : ""}
      disabled={disabled}
      handleEditorSetup={handleEditorSetup}
      handleSetContent={handleSetContent}
      editorRef={editorRef}
    />
  );
};

export default FormikRichTextEditor;
